import React, { useState } from 'react';
import { Card, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTools, 
  faUserShield, 
  faUser, 
  faExclamationTriangle,
  faCheckCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import { tenantService } from '../../services/tenantService';
import { auth, db } from '../../config/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const SystemUtilities = () => {
  const { userCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (title, status, message) => {
    setResults(prev => [...prev, { title, status, message, timestamp: new Date() }]);
  };

  const clearResults = () => {
    setResults([]);
  };

  // Fix Admin Permissions
  const fixAdminPermissions = async () => {
    setLoading(true);
    clearResults();
    
    try {
      addResult('Admin Permissions Fix', 'info', 'Starting admin permissions fix...');
      
      // Get current user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        addResult('Admin Permissions Fix', 'error', 'No user is currently signed in');
        return;
      }

      addResult('Admin Permissions Fix', 'info', `Checking user: ${currentUser.email}`);

      // Get user document
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        addResult('Admin Permissions Fix', 'error', 'User document does not exist');
        return;
      }

      const userData = userDoc.data();
      addResult('Admin Permissions Fix', 'info', `Current user data: ${JSON.stringify(userData)}`);

      // Check if permissions are missing
      if (!userData.permissions) {
        addResult('Admin Permissions Fix', 'warning', 'Permissions object is missing, adding...');
        
        const updatedData = {
          ...userData,
          permissions: ['manage_companies', 'manage_users', 'view_all_properties'],
          updatedAt: new Date()
        };

        await setDoc(userDocRef, updatedData, { merge: true });
        addResult('Admin Permissions Fix', 'success', 'Admin permissions restored successfully');
      } else {
        addResult('Admin Permissions Fix', 'success', 'Permissions already exist');
      }

    } catch (error) {
      console.error('Error fixing admin permissions:', error);
      addResult('Admin Permissions Fix', 'error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fix Tenant Creation Issues
  const fixTenantCreation = async () => {
    setLoading(true);
    clearResults();
    
    try {
      addResult('Tenant Creation Fix', 'info', 'Starting tenant creation fix...');
      
      if (!userCompany) {
        addResult('Tenant Creation Fix', 'error', 'Company ID not available');
        return;
      }

      addResult('Tenant Creation Fix', 'info', `Processing tenants for company: ${userCompany}`);

      // Get all tenants for the company
      const tenants = await tenantService.getTenantsByCompany(userCompany);
      addResult('Tenant Creation Fix', 'info', `Found ${tenants.length} tenants`);

      let fixedCount = 0;
      let errorCount = 0;

      for (const tenant of tenants) {
        try {
          addResult('Tenant Creation Fix', 'info', `Processing tenant: ${tenant.name} (${tenant.email})`);

          // Check if Firebase Auth user exists
          let authUid = tenant.authUid;
          
          if (!authUid) {
            addResult('Tenant Creation Fix', 'warning', `No authUid for ${tenant.email}, creating Firebase Auth user...`);
            
            try {
              // Create Firebase Auth user with email as password
              const userCredential = await createUserWithEmailAndPassword(auth, tenant.email, tenant.email);
              authUid = userCredential.user.uid;
              
              addResult('Tenant Creation Fix', 'success', `Created Firebase Auth user for ${tenant.email}`);
              
              // Update tenant document with authUid
              await updateDoc(doc(db, 'tenants', tenant.id), {
                authUid: authUid,
                updatedAt: new Date()
              });
              
            } catch (authError) {
              if (authError.code === 'auth/email-already-in-use') {
                addResult('Tenant Creation Fix', 'warning', `Firebase Auth user already exists for ${tenant.email}`);
                // Try to find the existing user
                const usersQuery = query(collection(db, 'users'), where('email', '==', tenant.email));
                const usersSnapshot = await getDocs(usersQuery);
                if (!usersSnapshot.empty) {
                  authUid = usersSnapshot.docs[0].id;
                  addResult('Tenant Creation Fix', 'info', `Found existing user document for ${tenant.email}`);
                }
              } else {
                addResult('Tenant Creation Fix', 'error', `Failed to create Firebase Auth user for ${tenant.email}: ${authError.message}`);
                errorCount++;
                continue;
              }
            }
          }

          // Check if user document exists
          if (authUid) {
            const userDocRef = doc(db, 'users', authUid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
              addResult('Tenant Creation Fix', 'warning', `Creating user document for ${tenant.email}...`);
              
              const userDocData = {
                name: tenant.name,
                email: tenant.email,
                role: 'tenant',
                companyId: tenant.companyId,
                status: 'active',
                createdAt: new Date(),
                updatedAt: new Date(),
                isTemporaryPassword: true,
                uid: authUid
              };

              await setDoc(userDocRef, userDocData);
              addResult('Tenant Creation Fix', 'success', `Created user document for ${tenant.email}`);
              fixedCount++;
            } else {
              addResult('Tenant Creation Fix', 'info', `User document already exists for ${tenant.email}`);
            }
          }

        } catch (error) {
          console.error(`Error processing tenant ${tenant.email}:`, error);
          addResult('Tenant Creation Fix', 'error', `Error processing ${tenant.email}: ${error.message}`);
          errorCount++;
        }
      }

      addResult('Tenant Creation Fix', 'success', `Completed! Fixed: ${fixedCount}, Errors: ${errorCount}`);

    } catch (error) {
      console.error('Error fixing tenant creation:', error);
      addResult('Tenant Creation Fix', 'error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'success',
      error: 'danger',
      warning: 'warning',
      info: 'info'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">System Utilities</h2>
          <p className="text-muted mb-0">Fix system issues and data inconsistencies</p>
        </div>
      </div>

      <Row>
        <Col lg={6}>
          {/* Admin Permissions Fix */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUserShield} className="text-danger me-2" />
                Fix Admin Permissions
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Restore missing permissions for admin users. This will add the permissions object 
                if it's missing from the current user's document.
              </p>
              <Button 
                variant="outline-danger" 
                onClick={fixAdminPermissions}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTools} className="me-2" />
                    Fix Admin Permissions
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          {/* Tenant Creation Fix */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="text-warning me-2" />
                Fix Tenant Creation Issues
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Fix tenants that exist in the database but don't have Firebase Auth users or user documents. 
                This will create the missing authentication and user records.
              </p>
              <Button 
                variant="outline-warning" 
                onClick={fixTenantCreation}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTools} className="me-2" />
                    Fix Tenant Creation
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Results */}
      {results.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-transparent border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Operation Results</h5>
              <Button variant="outline-secondary" size="sm" onClick={clearResults}>
                Clear Results
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-2">
              {results.map((result, index) => (
                <Alert key={index} variant={result.status === 'error' ? 'danger' : result.status === 'warning' ? 'warning' : result.status === 'success' ? 'success' : 'info'}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{result.title}</strong>
                      <br />
                      {result.message}
                    </div>
                    <small className="text-muted">
                      {result.timestamp.toLocaleTimeString()}
                    </small>
                  </div>
                </Alert>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default SystemUtilities; 